import * as React from "react";
import { graphql, useStaticQuery, withPrefix } from "gatsby";

// @ts-ignore
import favicon from "../assets/images/favicon.png";

interface Props {
  title: string;
  lang?: string;
  description?: string;
  index?: boolean; // false => noindex
  ogImage?: string;
  /**
   * Ruta calculada por Gatsby (location.pathname dentro de `export const Head`).
   * El canónico se deriva de aquí — ya NO depende del campo urlCanonical de Contentful.
   */
  pathname?: string;
}

const SEO = (props: Props) => {
  const { site, allFile } = useStaticQuery(graphql`
    query GET_SITE_METADATA {
      site {
        siteMetadata {
          title
          siteUrl
          description
          author
        }
      }
      allFile(filter: { name: { eq: "logo_1x1" } }) {
        nodes {
          relativePath
          publicURL
        }
      }
    }
  `);

  const { title, lang, description, index, ogImage, pathname } = props;

  // Origen del sitio sin slash final, p. ej. "https://www.comfama.com"
  const siteOrigin = site.siteMetadata.siteUrl.replace(/\/+$/, "");

  // Canónico = origen + ruta calculada por Gatsby (incluye el pathPrefix /ayuda).
  // En el build con --prefix-paths, location.pathname YA viene con el prefijo,
  // así que normalizamos para garantizar exactamente un /ayuda (ni doble ni ausente).
  const prefix = withPrefix("/").replace(/\/+$/, ""); // "/ayuda" en build, "" en dev
  let canonicalPath = pathname ?? "";
  if (
    prefix &&
    (canonicalPath === prefix || canonicalPath.startsWith(`${prefix}/`))
  ) {
    canonicalPath = canonicalPath.slice(prefix.length);
  }
  const canonicalUrl =
    pathname !== undefined ? `${siteOrigin}${prefix}${canonicalPath}` : "";

  const seoData = {
    lang: lang || "es",
    title: title || site.siteMetadata.title,
    description: description || site.siteMetadata.description,
    index: index ?? true,
    ogImageURL: ogImage
      ? `https:${ogImage}`
      : `${siteOrigin}${allFile.nodes[0].publicURL}`,
    author: site.siteMetadata.author,
  };

  return (
    <>
      <html lang={seoData.lang} />
      <title>{seoData.title}</title>
      <meta name="description" content={seoData.description} />

      <meta property="og:title" content={seoData.title} />
      <meta property="og:description" content={seoData.description} />
      <meta property="og:image" content={seoData.ogImageURL} />
      <meta property="og:image:width" content="400" />
      <meta property="og:image:height" content="300" />
      <meta property="og:type" content="website" />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:creator" content={seoData.author} />
      <meta name="twitter:title" content={seoData.title} />
      <meta name="twitter:description" content={seoData.description} />
      <meta name="twitter:image" content={seoData.ogImageURL} />

      <meta
        name="robots"
        content={
          seoData.index
            ? "index, follow, max-image-preview:large"
            : "noindex, nofollow"
        }
      />

      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
      />

      <link rel="icon" type="image/png" href={favicon} />

      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
    </>
  );
};

export default SEO;
